import { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";

import Stack from '@mui/joy/Stack';
import Accordion from '@mui/joy/Accordion';
import AccordionDetails from '@mui/joy/AccordionDetails';
import AccordionSummary from '@mui/joy/AccordionSummary';
import AccordionGroup from '@mui/joy/AccordionGroup';
import Typography from "@mui/joy/Typography";
import Divider from '@mui/joy/Divider';

import Tooltip from '@mui/joy/Tooltip';
import Chip from '@mui/joy/Chip';


import { CallIncomingType } from "stores/call_incoming_store";

import useUserIdentity from "hooks/useUserIdentity";
import useCallIncoming from "hooks/useCallIncoming";

import { TimeUI } from 'components/Date';
import { Success } from 'components/FancyToast';
import useIncomingPhoneNumber from "hooks/useIncomingPhoneNumber";

import { getIcon } from 'utils/misc';
import { pluralize } from 'utils/misc';
import { timeDuration } from 'utils/misc';
import { phoneFormat } from 'utils/misc';

import FileAssociation from "components/FileAssociation";

function IncomingCallItem({call}:{call: CallIncomingType}) {
    const PhoneIcon = getIcon('LocalPhone');
    return (
        <Stack >

            <Stack direction="row" justifyContent="space-between">
                <Typography level="body-sm">
                    <TimeUI ts={call?.date_created} />
                </Typography>

                    <Tooltip arrow title="Copy number to clipboard">
                        <Chip
                            startDecorator={<PhoneIcon />}
                            variant="outlined"
                            onClick={() => {
                                navigator.clipboard.writeText(call.number_from.replace('+1', ''))
                                Success("Copied phone number to clipboard");
                            }}>
                            {phoneFormat(call.number_from.replace('+1', ''))}
                        </Chip>
                    </Tooltip>

            </Stack>
            <Stack>



                {call.twilio_duration &&
                    <Typography level="body-sm">
                        { timeDuration(parseInt(call.twilio_duration)) }
                    </Typography>
                }

                { call.file_association &&
                    <Stack alignItems="center">
                        <FileAssociation uuid={call.file_association} />
                    </Stack>
                }

            </Stack>
        </Stack>
    );
}

const CallIncomingListByIncomingPhoneNumber = observer(({associated_type, associated_uuid}:{associated_type: string, associated_uuid: string} ) => {
    const {
        fetchListByIncomingPhoneNumber,
        getListByIncomingPhoneNumber,
        callIncomingItems,
    } = useCallIncoming();
    const { getOnly } = useIncomingPhoneNumber();
    const [items, setItems] = useState([] as CallIncomingType[]);
    const [fetched, setFetched] = useState(false);
    const { currentIdentity } = useUserIdentity();
    const PhoneIcon = getIcon('RingVolumeOutlined');
    const [expanded, setExpanded] = useState(false);
    const [loading, setLoading] = useState(false);
    const [ipn_uuid, setIpnUuid] = useState('');
    useEffect(() => {
        if (fetched) return;

        if (currentIdentity?.uuid && associated_type && associated_uuid) {
            setFetched(true);
            setLoading(true);

            getOnly(currentIdentity.uuid, associated_type, associated_uuid)
                .then((incoming_phone_number) => {
                    fetchListByIncomingPhoneNumber(currentIdentity.key, incoming_phone_number.uuid)
                        .then(() => {
                            setIpnUuid(incoming_phone_number.uuid);
                        }).finally(() => {
                            setLoading(false);
                        });
                }).finally(() => {
                    setLoading(false);
                });
        }
    }, [fetchListByIncomingPhoneNumber,
        getOnly,
        associated_type,
        associated_uuid,
        getListByIncomingPhoneNumber,
        currentIdentity?.key,
        setItems]);

    useEffect(() => {
        setItems(getListByIncomingPhoneNumber(ipn_uuid));
    }, [callIncomingItems.length, setItems, ipn_uuid]);

    return (
        <AccordionGroup size="lg">
            <Accordion
                expanded={expanded}
                onChange={() => setExpanded(!expanded)}
            >
                <AccordionSummary>
                    <PhoneIcon />
                    <Typography level="body-sm">
                        {loading ? 'Loading Incoming Calls' : pluralize(items?.length, 'Incoming Call')}
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Stack
                        spacing={1}
                        divider={<Divider />} >

                        {items.map(i =>
                            <IncomingCallItem key={i.uuid} call={i} />
                        )}
                    </Stack>
                </AccordionDetails>
            </Accordion>
        </AccordionGroup>

    );
 });


export default CallIncomingListByIncomingPhoneNumber;
